import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import { brand } from '../../theme';
import { formatBytes } from '../../utils/format';

const draggingFiles = (event: { dataTransfer: DataTransfer | null }) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

interface VideoDropZoneProps {
  /** The hidden file input; the owner reads and resets it. */
  inputRef: React.RefObject<HTMLInputElement>;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Files dropped on the zone. */
  onDropFiles: (files: FileList) => void;
  /** While an upload runs: no picking, no dropping. */
  disabled?: boolean;
  /** "primary" when the zone is the page's main action, "secondary" inside a page that already has one. */
  emphasis?: 'primary' | 'secondary';
  /** Formats, limits and what happens next. */
  hint: React.ReactNode;
  /** Lets a host move focus to the zone (e.g. when it opens the uploader). */
  zoneRef?: React.MutableRefObject<HTMLDivElement | null>;
}

/**
 * Drop a video here, or choose a file: the zone is one button (Enter or Space
 * opens the file picker) and a drop target. While it's on screen, files
 * dropped anywhere else on the page are ignored instead of making the browser
 * open the file - which would leave the app and drop any upload in progress.
 */
const VideoDropZone: React.FC<VideoDropZoneProps> = ({
  inputRef,
  onInputChange,
  onDropFiles,
  disabled = false,
  emphasis = 'primary',
  hint,
  zoneRef,
}) => {
  const titleId = useId();
  const hintId = useId();
  const elementRef = useRef<HTMLDivElement | null>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const setZoneElement = useCallback(
    (node: HTMLDivElement | null) => {
      elementRef.current = node;
      if (zoneRef) zoneRef.current = node;
    },
    [zoneRef]
  );

  useEffect(() => {
    const ignoreStrayDrop = (event: DragEvent) => {
      if (!draggingFiles(event)) return;
      if (elementRef.current && event.target instanceof Node && elementRef.current.contains(event.target)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'none';
    };
    window.addEventListener('dragover', ignoreStrayDrop);
    window.addEventListener('drop', ignoreStrayDrop);
    return () => {
      window.removeEventListener('dragover', ignoreStrayDrop);
      window.removeEventListener('drop', ignoreStrayDrop);
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      dragDepth.current = 0;
      setDragging(false);
    }
  }, [disabled]);

  const openPicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openPicker();
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
    if (disabled) return;
    dragDepth.current += 1;
    setDragging(true);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggingFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggingFiles(event)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setDragging(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    if (disabled) return;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) onDropFiles(files);
  };

  const primary = emphasis === 'primary';

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        hidden
        tabIndex={-1}
        onChange={onInputChange}
        disabled={disabled}
        data-testid="video-file-input"
      />
      <Box
        ref={setZoneElement}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-labelledby={titleId}
        aria-describedby={hintId}
        data-testid="video-drop-zone"
        data-dragging={dragging ? 'true' : undefined}
        onClick={openPicker}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 0.75,
          px: { xs: 2.5, sm: 4 },
          py: { xs: 4, sm: 5 },
          borderRadius: 3,
          border: `1.5px dashed ${dragging ? brand.copper : brand.borderStrong}`,
          bgcolor: dragging ? alpha(brand.copper, 0.05) : 'grey.100',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          transition: 'border-color 0.15s ease, background-color 0.15s ease',
          outline: 'none',
          '&:hover': disabled ? undefined : { bgcolor: dragging ? alpha(brand.copper, 0.05) : 'grey.200' },
          '&:hover .drop-zone-button': disabled
            ? undefined
            : primary
              ? { bgcolor: 'primary.dark' }
              : { borderColor: brand.copper },
          '&:focus-visible': { outline: `2px solid ${brand.copper}`, outlineOffset: 2 },
        }}
      >
        <CloudUploadOutlinedIcon aria-hidden="true" sx={{ fontSize: 32, color: brand.slate, mb: 0.5 }} />
        <Typography id={titleId} component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {dragging ? 'Drop the video to upload it' : 'Drop a video here, or choose a file'}
        </Typography>
        <Typography id={hintId} component="span" variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          {hint}
        </Typography>
        <Box
          aria-hidden="true"
          className="drop-zone-button"
          sx={{
            mt: 1.5,
            px: 2.25,
            py: 0.875,
            borderRadius: 2,
            fontSize: '0.875rem',
            fontWeight: 700,
            lineHeight: 1.75,
            transition: 'background-color 0.15s ease, border-color 0.15s ease',
            ...(primary
              ? { bgcolor: 'primary.main', color: 'primary.contrastText' }
              : { bgcolor: 'background.paper', color: 'text.primary', border: `1px solid ${brand.borderStrong}` }),
          }}
        >
          Choose file
        </Box>
      </Box>
    </>
  );
};

interface UploadFileRowProps {
  name: string;
  size: number;
  /** 0 while getting ready, then 1-100. */
  progress: number;
  /** Offered once the transfer has started. */
  onCancel?: () => void;
}

const progressText = (progress: number) => {
  if (progress <= 0) return 'Getting ready…';
  if (progress >= 99) return 'Finishing up…';
  return `${Math.round(progress)}% uploaded`;
};

/** The file being uploaded: name, size, progress and Cancel. */
export const UploadFileRow: React.FC<UploadFileRowProps> = ({ name, size, progress, onCancel }) => (
  <Box
    data-testid="upload-file-row"
    sx={{ mt: 2, px: 2, py: 1.5, border: `1px solid ${brand.border}`, borderRadius: 2, bgcolor: 'background.paper' }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <VideoFileOutlinedIcon aria-hidden="true" sx={{ color: brand.slate, fontSize: 22 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap title={name} sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatBytes(size)} · {progressText(progress)}
        </Typography>
      </Box>
      {onCancel && (
        <Button size="small" color="inherit" onClick={onCancel} data-testid="cancel-upload-button">
          Cancel
        </Button>
      )}
    </Box>
    <LinearProgress
      variant={progress > 0 ? 'determinate' : 'indeterminate'}
      value={progress > 0 ? progress : undefined}
      color="secondary"
      aria-label={`Uploading ${name}`}
      sx={{ mt: 1.25, height: 6, borderRadius: 3 }}
    />
  </Box>
);

export default VideoDropZone;
